<template>
  <v-card
    :class="{ new: isCurrentStatusNew(localItem) || isCurrentStatusCreated(localItem) }"
    class="mx-auto lead-card"
  >
    <DueDateCalendar
      v-if="showDueDateCalendar"
      :custom-class="'lead-card-calendar'"
      :due-date="localItem.dueDate"
      @close="showDueDateCalendar = false"
      @save="handleSaveDueDate($event)"
      @delete="handleDeleteDueDate()"
    />
    <v-card-text class="d-flex col-12 pl-2">
      <div
        class="content-wrapper"
        @click="openContactDetails(localItem)"
      >
        <v-card-title
          class="pt-0 pl-0 title"
          style="display: block"
        >
          <template v-if="hasName(localItem)">
            <span>{{ formatName(localItem) }}</span>
          </template>
          <template v-else>
            <span>{{ localItem.email }} </span>
          </template>
          <v-tooltip
            v-if="isEmailConfirmedVisible"
            location="top"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-if="localItem.confirmedAt"
                color="success"
                style="font-size: 16px"
                v-bind="props"
              >
                mdi-check-circle
              </v-icon>
              <v-icon
                v-else
                style="font-size: 16px"
                color="orange lighten-2"
                v-bind="props"
              >
                mdi-clock
              </v-icon>
            </template>
            <span v-if="localItem.confirmedAt">{{ $t('leads.table.labels.confirmed') }}</span>
            <span v-else>{{ $t('leads.table.labels.not-confirmed') }}</span>
          </v-tooltip>
        </v-card-title>
        <v-card-subtitle class="px-0 d-md-flex subtitle">
          <v-tooltip
            v-if="localItem.lastConversionAt && isLastConversionAtVisible"
            location="top"
          >
            <template v-slot:activator="{ props }">
              <div v-bind="props">
                <span>
                  <v-icon
                    class="mr-1"
                    size="16"
                  >
                    mdi-update
                  </v-icon>
                  <span class="last-activity mr-3">{{
                    formatDate(localItem.lastConversionAt, 'dateShort')
                  }}</span>
                </span>
              </div>
            </template>
            <span class="last-activity-tooltip">{{ $t('lead.card.tooltip.last-activity') }}</span>
          </v-tooltip>

          <DueDate
            v-if="isDueDateVisible"
            :item="localItem"
            @click="toggleDueDateCalendar"
          />
        </v-card-subtitle>
      </div>
      <div
        v-if="isAssignedUserVisible"
        class="user-section"
      >
        <v-tooltip
          v-if="localItem.assignedUser"
          location="top"
        >
          <template v-slot:activator="{ props }">
            <div
              style="min-width: 30px; text-align: end"
              v-bind="props"
            >
              <v-menu
                max-width="260"
                max-height="280"
                location="bottom end"
                offset="4 8"
              >
                <template #activator="{ props: menuProps }">
                  <v-avatar
                    v-bind="menuProps"
                    :color="localItem.assignedUser.color"
                    size="30"
                    @click="showUsersForSelect = !showUsersForSelect"
                  >
                    <span class="text-white text-caption font-weight-bold">
                      {{ getInitials(localItem.assignedUser) }}
                    </span>
                  </v-avatar>
                </template>
                <AssignLead
                  :item="localItem"
                  :users="users"
                  @assignUser="assignUser"
                />
              </v-menu>
            </div>
          </template>
          <span v-if="localItem.assignedUser">{{ formatName(localItem.assignedUser) }}</span>
        </v-tooltip>

        <v-tooltip
          v-else
          location="top"
        >
          <template v-slot:activator="{ props }">
            <div v-bind="props">
              <v-menu
                max-width="260"
                max-height="280"
                location="bottom end"
                offset="4 8"
              >
                <template #activator="{ props: menuProps }">
                  <v-icon
                    v-bind="menuProps"
                    size="36"
                    color="grey-lighten-2"
                  >
                    mdi-account-circle avatar
                  </v-icon>
                </template>
                <AssignLead
                  :item="localItem"
                  :users="users"
                  @assignUser="assignUser"
                />
              </v-menu>
            </div>
          </template>
          <span>{{ $t('assign-leads.leads-for-select-tooltip') }}</span>
        </v-tooltip>
      </div>
    </v-card-text>
    <v-card-text
      v-if="areConversionsVisible"
      style="padding-left: 5px; padding-top: 0; padding-bottom: 0"
    >
      <div
        v-for="{ type, label, color } of conversionTypes"
        :key="type"
      >
        <v-tooltip location="top">
          <template v-slot:activator="{ on }">
            <v-chip
              v-if="showAction(localItem, type)"
              :color="isPropertyRequestUrgent(localItem, type) ? 'red-lighten-4' : color"
              size="x-small"
              variant="flat"
              class="ml-1 float-left mb-2"
              rounded
              v-on="isPropertyRequestUrgent(localItem, type) ? on : {}"
            >
              <span class="text-black">{{ $t(label) }}</span>
            </v-chip>
          </template>
          <span>{{
            isPropertyRequestUrgent(localItem, type) ? $t('leads.table.tooltips.start-asap') : ''
          }}</span>
        </v-tooltip>
      </div>
    </v-card-text>
    <v-card-text
      v-if="areTagsVisible && localItem.tags && localItem.tags.length > 0"
      class="tag-chips"
    >
      <template>
        <v-chip
          v-for="(tag, index) in crmTags(localItem.tags)"
          :key="index"
          color="#F0F0F0"
          size="x-small"
          variant="flat"
          rounded
          class="ml-1 float-left mb-2"
        >
          {{ tag }}
        </v-chip>
      </template>
    </v-card-text>
    <v-card-text
      opacity="0.6"
      class="d-flex justify-start py-0 property-value"
    >
      <div
        v-if="isValuationVisible && getPropertyValue(localItem)"
        class="mr-2 pt-0"
      >
        <v-icon class="pa-1 property-value-icons"> mdi mdi-home-analytics </v-icon>
        <span>{{ getPropertyValue(localItem) }}</span>
      </div>
      <div v-if="isCommissionVisible && getCommissionValue(localItem, commission)">
        <v-icon class="pa-1 property-value-icons"> mdi mdi-percent </v-icon>
        <span>{{ getCommissionValue(localItem, commission) }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { isBasicUser, isLightUser } from '@/lib/productTypes'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import currencyMixin from '@/mixins/currency'
import AssignLead from '@/modules/leads/LeadList/AssignLead.vue'
import DueDate from '@/modules/leads/LeadList/Cards/DueDate.vue'
import DueDateCalendar from '@/modules/leads/LeadList/Cards/DueDateCalendar.vue'
import leadsDueDateMixin from '@/modules/leads/LeadList/Cards/mixins/leadsDueDateMixin'
import { crmTags } from '@/modules/leads/LeadList/lib/crmTags'
import {
  commissionValue,
  hasPropertyValue,
  propertyValue,
} from '@/modules/leads/LeadList/lib/property'
import nameMixin from '@/modules/leads/mixins/name'
import propertyRequestMixin from '@/modules/leads/mixins/propertyRequest'
import showActionMixin from '@/modules/leads/mixins/showAction'

export default {
  components: { DueDate, AssignLead, DueDateCalendar },
  mixins: [currencyMixin, propertyRequestMixin, showActionMixin, nameMixin, leadsDueDateMixin],
  props: {
    commission: {
      type: String,
      default: '',
    },
    item: {
      type: Object,
      required: true,
    },
    users: {
      type: Array,
      default: () => [],
    },
    conversionTypes: {
      type: Array,
      required: true,
    },
    isLastConversionAtVisible: {
      type: Boolean,
      required: true,
    },
    areConversionsVisible: {
      type: Boolean,
      required: true,
    },
    isDueDateVisible: {
      type: Boolean,
      required: true,
    },
    isEmailConfirmedVisible: {
      type: Boolean,
      required: true,
    },
    isAssignedUserVisible: {
      type: Boolean,
      required: true,
    },
    areTagsVisible: {
      type: Boolean,
      required: true,
    },
    isValuationVisible: {
      type: Boolean,
      required: true,
    },
    isCommissionVisible: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      localItem: { ...this.item },
      showUsersForSelect: false,
      showDueDateCalendar: false,
    }
  },
  computed: {
    isBasicUser() {
      return isBasicUser(this.$auth.user)
    },
    isLightUser() {
      return isLightUser(this.$auth.user)
    },
  },
  methods: {
    hasPropertyValue,
    crmTags,
    async handleSaveDueDate(event) {
      const result = await this.setDueDate({ lead: { ...this.localItem }, date: event })
      if (this.localItem.id === result.id) {
        this.localItem = { ...this.localItem, dueDate: result.dueDate }
      }
    },
    async handleDeleteDueDate() {
      const result = await this.deleteDueDate({ lead: { ...this.localItem } })
      if (this.localItem.id === result.id) {
        this.localItem = { ...this.localItem, dueDate: undefined }
      }
    },
    getPropertyValue(item) {
      return propertyValue(item, this.formatPrice)
    },

    getCommissionValue(item, commission) {
      return commissionValue(item, commission, this.formatPrice)
    },
    async assignUser(userForSelect, id) {
      if (userForSelect.value === this.localItem?.assignedUser?.id) {
        showSnackbarMessage(
          'error',
          this.$t('alerts.assign-leads.already-assigned', { userName: userForSelect.text })
        )
        return
      }

      this.$emit('assignUser', id, userForSelect)
    },
    openContactDetails(contact) {
      const routeData = this.$router.resolve({
        name: 'lead-details',
        params: {
          id: contact.id,
        },
      })

      if (window.self !== window.top) {
        // Running inside an iframe (e.g. in the CRM)
        this.$router.push(routeData.href)
      } else {
        window.open(routeData.href, '_blank')
      }
    },
    isCurrentStatusNew(item) {
      return item.currentStatus === 'new'
    },
    isCurrentStatusCreated(item) {
      return item.currentStatus === 'created'
    },
    toggleDueDateCalendar() {
      this.$nextTick(() => {
        this.showDueDateCalendar = !this.showDueDateCalendar
      })
    },
  },
  watch: {
    item: {
      handler(newValue) {
        this.localItem = { ...newValue }
      },
    },
  },
  emits: ['assignUser'],
}
</script>

<style scoped>
.lead-card {
  cursor: pointer !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 4px !important;
  box-shadow: none !important;
  background: #ffffff;
  max-width: 400px;
  overflow: visible;
  z-index: revert-layer;
  position: relative;
}

.card-content {
  display: flex;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
}

.content-wrapper {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}

.content-wrapper :deep(.v-card-title) span {
  white-space: wrap;
}

.user-section {
  flex-shrink: 0;
  width: auto;
}

.lead-card:hover {
  box-shadow: 5px 5px 10px 0 rgba(20, 19, 30, 0.15) !important;
}

.title {
  color: #102a21;
  font-size: 16px !important;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
}
.subtitle {
  color: #102a21;
  font-size: 12px !important;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.new {
  border-top: 4px solid #e41d1d;
}

.tag-chips {
  padding: 0 0 0 5px;
}

.property-value {
  padding-left: 5px;
  margin-bottom: 5px;
  font-weight: 700;
}

.property-value span {
  font-size: 12px;
}

.property-value-icons {
  font-size: 16px;
}

.last-activity-tooltip {
  color: white;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.last-activity {
  color: #9e9e9e;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

:deep(.v-card-text) {
  width: 100%;
}
:deep(.bg-red-lighten-4) {
  background-color: #ff9fa7 !important;
}
</style>
